<template>
  <div :class="showConfigModules">
    <div :class="leftColumnClass">
      <div
        class="border-beige-10 flex h-12 items-center justify-between border-t border-solid bg-white p-3 pl-4"
      >
        <div class="flex items-center gap-2">
          <button
            :class="[
              'text-buttons',
              activeTab === 'properties' ? 'text-beige-90' : 'text-beige-40 hover:text-beige-70',
            ]"
            @click="setTab('properties')"
          >
            Properties
          </button>
          <template v-if="aiStyleStudioEnabled">
            <span class="text-beige-20">|</span>
            <button
              :class="[
                'text-buttons flex items-center gap-1',
                activeTab === 'ai' ? 'text-beige-90' : 'text-beige-40 hover:text-beige-70',
              ]"
              @click="setTab('ai')"
            >
              Design with AI
              <RfSparklesIcon class="h-4 w-4" />
            </button>
          </template>
        </div>
        <button v-if="leftColumnOpenStatus" @click="showHideLeftCol">
          <RfLeftPanelCloseIcon class="fill-beige-50 h-6 w-6" />
        </button>
      </div>
      <RfRetentionActionsInputs
        v-if="activeTab === 'properties'"
        :actions="actions"
        :model="model"
        :viewTypeTab="viewTypeTab"
        @forceRender="forceRender"
      />
      <div
        v-if="aiStyleStudioEnabled"
        v-show="activeTab === 'ai'"
        class="h-[calc(100%-3rem)] overflow-hidden"
      >
        <RfAiDesignChat :key="model.id" :model="model" :actions="actions" />
      </div>
    </div>

    <div class="retention-editor-rightcol h-full">
      <Transition>
        <button
          v-if="!leftColumnOpenStatus"
          class="absolute top-0 left-2 z-10 inline-flex h-12.5 w-8 items-center justify-center"
          @click="showHideLeftCol"
        >
          <RfLeftPanelOpenIcon class="fill-white-1 h-6 w-6" />
        </button>
      </Transition>
      <RfStandalonePreview
        v-if="isStandAlone"
        :key="previewKey"
        ref="standalone"
        :model="model"
        :actions="actions"
        :actionGroupId="model.action_group_id"
        :viewTypeTab="viewTypeTab"
        :webPreviewClass="standAlonePreviewClass"
        @clickTab="clickTab"
      />
      <div
        v-if="model.path_type === 'text'"
        class="rf-retention-modal-preview retention-text-preview"
      >
        <RfRetentionTileText
          v-if="model.path_type === 'text'"
          :key="previewKey"
          ref="text"
          :actions="actions"
          :model="model"
          :viewTypeTab="viewTypeTab"
          :currPathGroup="currPathGroup"
          @clickTab="clickTab"
        />
      </div>
      <div
        v-if="isHorizontalVerticalTile"
        class="rf-retention-modal-preview retention-tile-preview"
      >
        <RfRetentionTile
          :key="previewKey"
          ref="tile"
          :actions="actions"
          :viewTypeTab="viewTypeTab"
          :model="model"
          :currPathGroup="currPathGroup"
          @clickTab="clickTab"
        />
      </div>
      <div v-if="isEmail(model)">
        <RfRetentionEmail
          :key="previewKey"
          ref="tile"
          :actions="actions"
          :viewTypeTab="viewTypeTab"
          :model="model"
          :currPathGroup="currPathGroup"
          @clickTab="clickTab"
        />
      </div>
      <div v-if="isPushNotification">
        <RfRetentionPush
          ref="scheduled_push"
          :key="previewKey"
          :model="model"
          :actions="actions"
          :viewTypeTab="viewTypeTab"
          @clickTab="clickTab"
        />
      </div>
    </div>
  </div>
</template>

<script>
import RfSparklesIcon from "@/components/icons/RfSparklesIcon.vue";
import RfRetentionTile from "@/components/RfPathCreate/RfRetentionActions/RfRetentionTile.vue";
import RfRetentionTileText from "@/components/RfPathCreate/RfRetentionActions/RfRetentionTileText.vue";
import RfStandalonePreview from "@/components/RfPathCreate/RfRetentionDevices/RfStandalonePreview.vue";
import RfRetentionEmail from "@/components/RfPathCreate/RfRetentionActions/RfRetentionEmail.vue";
import RfRetentionActionsInputs from "@/components/RfPathCreate/RfRetentionActions/RfRetentionActionsInputs.vue";
import { cloneDeep } from "lodash-es";
import { isEmail, isPushNotification } from "@/utils/prompts/promptHelpers";
import { PromptTypes } from "@/utils/constants/PromoTypesConstants";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import RfRetentionPush from "./RfRetentionActions/RfRetentionPush.vue";
import { isLatestExperimentActive } from "@/utils/prompts/promptHelpers";
import { useExperimentsStore } from "@/pinia/experimentsStore";
import { computed, onMounted } from "vue";
import RfLeftPanelCloseIcon from "../icons/RfLeftPanelCloseIcon.vue";
import RfLeftPanelOpenIcon from "../icons/RfLeftPanelOpenIcon.vue";
import { useLiquidContextStore } from "@/pinia/liquidContextStore";
import RfAiDesignChat from "./RfAiDesignChat.vue";

export default {
  name: "RfRetentionCreativeEditor",
  components: {
    RfRetentionTile,
    RfRetentionTileText,
    RfStandalonePreview,
    RfRetentionEmail,
    RfRetentionActionsInputs,
    RfRetentionPush,
    RfLeftPanelCloseIcon,
    RfLeftPanelOpenIcon,
    RfAiDesignChat,
    RfSparklesIcon,
  },
  props: ["model"],
  setup: props => {
    const liquidContextStore = useLiquidContextStore();
    onMounted(() => liquidContextStore.init());
    return {
      isEmail,
      isPushNotification: computed(() => isPushNotification(props.model)),
      PromptTypes,
      allPromptsStore: useAllPromptsStore(),
      appsStore: useAppsStore(),
      userStore: useUserStore(),
      experimentsStore: useExperimentsStore(),
    };
  },
  data() {
    return {
      viewTypeTab: this.isPushNotification ? "mobilewebViewTab" : "desktopViewTab",
      previewKey: 0,
      actions: this.cloneActions(this.model),
      leftColumnOpenStatus: true,
      path: this.model,
      activeTab: "properties",
    };
  },
  computed: {
    aiStyleStudioEnabled() {
      return (
        (!!this.appsStore.app?.flags?.ai_style_studio_internal &&
          this.userStore.isCurrentSuperAdmin) ||
        !!this.appsStore.app?.flags?.ai_style_studio
      );
    },
    currPathGroup() {
      return this.allPromptsStore.promptGroups.find(item => item.id === this.model.path_group_id);
    },
    isStandAlone() {
      const pathType = this.model.path_type;
      return (
        pathType === "retention_modal" ||
        pathType === "video" ||
        pathType === "interstitial" ||
        pathType === "widget" ||
        pathType === "bottom_banner"
      );
    },
    isHorizontalVerticalTile() {
      const pathType = this.model.path_type;
      return pathType === "horizontal" || pathType === "tile" || pathType === "vertical";
    },
    showConfigModules() {
      if (this.leftColumnOpenStatus) {
        return "configmodules-visible retention-editor-wrapper";
      }
      return "retention-editor-wrapper";
    },
    isLeftColumnOpen() {
      return this.leftColumnOpenStatus;
    },
    latestExperimentActive() {
      return isLatestExperimentActive({
        path: this.path,
        experiments: this.experimentsStore.experiments,
      });
    },
    leftColumnClass() {
      return {
        "retention-editor-leftcol": true,
        "open-module": this.isLeftColumnOpen,
        "close-module": !this.isLeftColumnOpen,
        "inputs-disabled": this.latestExperimentActive || this.userStore.isReadOnly,
      };
    },
    standAlonePreviewClass() {
      if (this.model.path_type === "video") return "retention-video-preview-div";
      return "retention-web-preview-div";
    },
  },
  watch: {
    model(to) {
      this.actions = this.cloneActions(to);
    },
  },
  mounted() {
    this.actions = cloneDeep(this.path.actions);
  },
  methods: {
    async fillModel(model) {
      model.actions = this.actions;

      if (this.$refs.standalone) {
        await this.$refs.standalone.fillModel(model);
      }

      if (this.$refs.tile) {
        await this.$refs.tile.fillModel(model);
      }

      if (this.$refs.text) {
        await this.$refs.text.fillModel(model);
      }
    },
    cloneActions(model) {
      return model ? cloneDeep(model.actions) : {};
    },
    forceRender() {
      this.previewKey += 1;
    },
    clickTab(tabType) {
      this.viewTypeTab = tabType;
    },
    showHideLeftCol() {
      this.leftColumnOpenStatus = !this.leftColumnOpenStatus;
    },
    setTab(tab) {
      this.activeTab = tab;
    },
  },
};
</script>
